import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  People as PeopleIcon,
  AccessTime as AccessTimeIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  CartesianGrid
} from 'recharts';

const COLORS = ['#00e5ff', '#ff4081', '#ffab00', '#00e676', '#2196f3'];

export default function PlayerActivityDashboard() {
  const [days, setDays] = useState(30);
  const navigate = useNavigate();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['player-activity', days],
    queryFn: async () => {
      const response = await api.get(`/dashboard/player-activity?days=${days}`);
      return response.data;
    },
    refetchInterval: 60000 // Refresh every minute
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        Failed to load player activity: {error?.message || 'Unknown error'}
      </Alert>
    );
  }

  const {
    currentActive = 0,
    lastHourActive = 0,
    todayStats = { sessionsToday: 0, uniqueUsersToday: 0 },
    sessionsPerDay = [],
    activePlayersPerDay = [],
    authMethodBreakdown = [],
    topPlayers = []
  } = data || {};

  // Merge sessions and active players data for combined chart
  const combinedDailyData = sessionsPerDay.map(session => {
    const active = activePlayersPerDay.find(ap => ap.date === session.date);
    return {
      date: new Date(session.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      sessions: session.sessionCount,
      uniqueUsers: session.uniqueUsers,
      activePlayers: active?.activePlayers || 0
    };
  });

  const handleUserClick = (userId) => {
    navigate(`/user/${userId}`);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Player Activity Dashboard
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={days}
              label="Time Period"
              onChange={(e) => setDays(e.target.value)}
            >
              <MenuItem value={7}>Last 7 days</MenuItem>
              <MenuItem value={30}>Last 30 days</MenuItem>
              <MenuItem value={90}>Last 90 days</MenuItem>
            </Select>
          </FormControl>
          <IconButton onClick={() => refetch()} disabled={isLoading}>
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Stack>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'success.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <PeopleIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {lastHourActive.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Online (1h)
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <PeopleIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {currentActive.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Now (24h)
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'info.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <AccessTimeIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {todayStats.sessionsToday.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sessions Today
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'secondary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <PersonIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {todayStats.uniqueUsersToday.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Unique Users Today
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'warning.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <TrendingUpIcon />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {sessionsPerDay.length > 0
                      ? Math.round(
                          sessionsPerDay.reduce((sum, d) => sum + d.sessionCount, 0) /
                            sessionsPerDay.length
                        ).toLocaleString()
                      : 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Sessions/Day
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Sessions & Active Players Over Time
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={combinedDailyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                <XAxis
                  dataKey="date"
                  stroke="rgba(255, 255, 255, 0.7)"
                  tick={{ fill: 'rgba(255, 255, 255, 0.7)' }}
                />
                <YAxis
                  stroke="rgba(255, 255, 255, 0.7)"
                  tick={{ fill: 'rgba(255, 255, 255, 0.7)' }}
                />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid rgba(0, 229, 255, 0.3)',
                    borderRadius: '8px',
                    color: '#ffffff'
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="sessions"
                  stroke="#00e5ff"
                  strokeWidth={2}
                  name="Sessions"
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="uniqueUsers"
                  stroke="#ff4081"
                  strokeWidth={2}
                  name="Unique Users"
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="activePlayers"
                  stroke="#00e676"
                  strokeWidth={2}
                  name="Active Players"
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Authentication Methods
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={authMethodBreakdown}
                  dataKey="count"
                  nameKey="authType"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ authType, count }) => `${authType}: ${count}`}
                >
                  {authMethodBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid rgba(0, 229, 255, 0.3)',
                    borderRadius: '8px',
                    color: '#ffffff'
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Top Players Table */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Top Active Players ({days} days)
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Rank</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Wallet</TableCell>
                <TableCell align="right">Sessions</TableCell>
                <TableCell>Last Session</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topPlayers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No player data available
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                topPlayers.map((player, index) => (
                  <TableRow
                    key={player.id}
                    sx={{
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
                      cursor: 'pointer'
                    }}
                    onClick={() => handleUserClick(player.id)}
                  >
                    <TableCell>
                      <Chip
                        label={`#${index + 1}`}
                        size="small"
                        color={index < 3 ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {player.name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontSize="0.75rem">
                        {player.email || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                        {player.wallet
                          ? `${player.wallet.substring(0, 6)}...${player.wallet.substring(38)}`
                          : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">
                        {player.sessionCount.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontSize="0.75rem">
                        {player.lastSession
                          ? new Date(player.lastSession).toLocaleString()
                          : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUserClick(player.id);
                        }}
                      >
                        <PersonIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
